import React, { useState, useEffect } from 'react';
import { SettingsComponentProps } from '../../SettingsRouter.component';
import { FiChevronDown } from 'react-icons/fi';
import RaptorDatePicker from '../../../../components/selects/RaptorDatePicker.component';
import dayjs from 'dayjs';
import { getAllRelevantDates } from '../../../../utilities/dateUtilities';
import ExportButton from '../../../feedback/ExportButton';
import { useDispatch } from 'react-redux';
import { addComponentToPdfExport } from '../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../types/redux/pdfExports/FileExportsStore';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import GenericModal from '../../../ui/GenericModal';
import { useAuditLogData } from '../../../../api/rs-hg/audit-log/hooks/queries';
import { AuditLogEntry } from '../../../../api/rs-hg/audit-log/types';

const AuditLog: React.FC<SettingsComponentProps> = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageInputValue, setPageInputValue] = useState('');

  const [startDate, setStartDate] = useState<Date>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  );
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalValue, setModalValue] = useState('');

  const {
    data: auditLogs,
    isLoading,
    isFetching,
    isError,
  } = useAuditLogData(startDate, endDate);

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
      setPage(0);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
      setPage(0);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setPageInputValue('');
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setPageInputValue('');
  };

  const handlePageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageInputValue(event.target.value);
  };

  const openValueModal = (title: string, value: string) => {
    setModalTitle(title);
    setModalValue(value);
    setModalOpen(true);
  };

  const tablePdfExportHandler = async () => {
    const renderColumns = [
      { header: 'Timestamp', dataKey: 'endpoint_execution_datetime' },
      { header: 'User', dataKey: 'user_name' },
      { header: 'Operation', dataKey: 'operation' },
      { header: 'Page', dataKey: 'page_name' },
      { header: 'Attribute', dataKey: 'attribute_key' },
      { header: 'Before Change', dataKey: 'value_before_change' },
      { header: 'After Change', dataKey: 'value_after_change' },
      { header: 'Client Name', dataKey: 'client_name' },
      { header: 'Fund Name', dataKey: 'fund_name' },
      { header: 'Position Name', dataKey: 'position_name' },
      { header: 'Key Path', dataKey: 'key_path' },
    ];

    return {
      startY: 50,
      columns: renderColumns,
      body: auditLogs ?? [],
    };
  };

  useEffect(() => {
    if (auditLogs && auditLogs.length > 0) {
      dispatch(
        addComponentToPdfExport({
          identifier: `audit_log_table_${dayjs(startDate).format('YYYY-MM-DD')}_to_${dayjs(endDate).format('YYYY-MM-DD')}`,
          handler: tablePdfExportHandler,
          type: PdfComponentType.TABLE,
          title: `Audit Log (${dayjs(startDate).format('YYYY-MM-DD')} to ${dayjs(endDate).format('YYYY-MM-DD')})`,
          dontMoveToNewPage: true,
        }),
      );
    }
  }, [auditLogs, dispatch, startDate, endDate]);

  return (
    <>
      <div className="flex w-full flex-col gap-2 p-1.5">
        <div className="rounded-md border border-gray-200 bg-white p-4 shadow-md">
          <div className="mb-2 flex items-center justify-between">
            <h1 className="text-xl font-bold text-mainblue underline">
              Audit Log
            </h1>
          </div>
          <p className="mb-4 text-base text-mainblue">
            View a history of changes made to system data and settings. This is
            an alpha feature and is subject to change/updates. Please report any
            irregularities found.
          </p>

          <div className="flex w-fit gap-4 rounded-md border border-mainblue border-opacity-20 bg-mainblue bg-opacity-10">
            <div className="flex-shrink-0">
              <RaptorDatePicker
                title="Start Date:"
                selectedDate={dayjs(startDate)}
                datesToInclude={getAllRelevantDates()}
                handler={handleStartDateChange}
              />
            </div>
            <div className="flex-shrink-0">
              <RaptorDatePicker
                title="End Date:"
                selectedDate={dayjs(endDate)}
                datesToInclude={getAllRelevantDates()}
                handler={handleEndDateChange}
              />
            </div>
          </div>
        </div>

        <QueryBoundary
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          data={auditLogs}
          loadingMessages={[
            'Loading audit log data...',
            'Just a few more seconds...',
          ]}
          errorMessage="Error loading audit log data. Please try again later."
          noDataMessage="No audit log entries found for the selected date range."
        >
          {(logs) => (
            <AuditLogTable
              logs={logs}
              page={page}
              rowsPerPage={rowsPerPage}
              pageInputValue={pageInputValue}
              startDate={startDate}
              endDate={endDate}
              onOpenValueModal={openValueModal}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              onPageInputChange={handlePageInputChange}
              onPageInputKeyDown={(event, totalPages) => {
                if (event.key === 'Enter') {
                  const pageNumber = parseInt(pageInputValue, 10);
                  if (
                    !isNaN(pageNumber) &&
                    pageNumber > 0 &&
                    pageNumber <= totalPages
                  ) {
                    handleChangePage(pageNumber - 1);
                  }
                }
              }}
            />
          )}
        </QueryBoundary>
      </div>

      <GenericModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        title={modalTitle}
        className="max-w-2xl w-full"
      >
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-sm">
          {modalValue}
        </pre>
      </GenericModal>
    </>
  );
};

interface AuditLogTableProps {
  logs: AuditLogEntry[];
  page: number;
  rowsPerPage: number;
  pageInputValue: string;
  startDate: Date;
  endDate: Date;
  onOpenValueModal: (title: string, value: string) => void;
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onPageInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPageInputKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
    totalPages: number,
  ) => void;
}

function AuditLogTable({
  logs,
  page,
  rowsPerPage,
  pageInputValue,
  startDate,
  endDate,
  onOpenValueModal,
  onChangePage,
  onChangeRowsPerPage,
  onPageInputChange,
  onPageInputKeyDown,
}: AuditLogTableProps) {
  const totalPages = Math.ceil(logs.length / rowsPerPage);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 shadow-md">
      <div className="mb-2 flex justify-end">
        <ExportButton
          exportData={logs}
          fields={[
            'endpoint_execution_datetime',
            'user_name',
            'operation',
            'page_name',
            'attribute_key',
            'value_before_change',
            'value_after_change',
            'client_name',
            'fund_name',
            'position_name',
            'key_path',
          ]}
          fileName={`audit_log_${dayjs(startDate).format('YYYY-MM-DD')}_to_${dayjs(endDate).format('YYYY-MM-DD')}`}
          allowPdfExport={true}
          pdfIdentifier={`audit_log_table_${dayjs(startDate).format('YYYY-MM-DD')}_to_${dayjs(endDate).format('YYYY-MM-DD')}`}
        />
      </div>
      <div className="overflow-x-auto border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Operation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Page
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Fund Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Position Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Attribute
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                Before
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
                After
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {logs
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((log, index) => (
                <tr key={index} className="hover:bg-blue-50">
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {
                      new Date(log.endpoint_execution_datetime)
                        .toISOString()
                        .split('T')[0]
                    }{' '}
                    {
                      new Date(log.endpoint_execution_datetime)
                        .toTimeString()
                        .split(' ')[0]
                    }
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {log.user_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${log.operation === 'create'
                        ? 'bg-green-100 text-green-800'
                        : log.operation === 'update'
                          ? 'bg-blue-100 text-blue-800'
                          : log.operation === 'delete'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      {log.operation}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {log.page_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {log.fund_name}
                  </td>
                  <td
                    className="max-w-xs whitespace-normal break-words align-top px-4 py-4 text-sm text-gray-600"
                    title={log.position_name || undefined}
                  >
                    {log.position_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    {log.attribute_key}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    <div
                      className="max-w-xs cursor-pointer truncate hover:text-mainblue hover:underline"
                      title="Click to view full value"
                      onClick={() =>
                        onOpenValueModal(
                          'Before Change',
                          log.value_before_change,
                        )
                      }
                    >
                      {log.value_before_change}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                    <div
                      className="max-w-xs cursor-pointer truncate hover:text-mainblue hover:underline"
                      title="Click to view full value"
                      onClick={() =>
                        onOpenValueModal(
                          'After Change',
                          log.value_after_change,
                        )
                      }
                    >
                      {log.value_after_change}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between rounded-b-md border border-gray-200 bg-gray-50 px-4 py-3 sm:px-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onChangePage(page - 1)}
            disabled={page === 0}
            className={`relative inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium ${page === 0 ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
          >
            Previous
          </button>
          <button
            onClick={() => onChangePage(page + 1)}
            disabled={page >= totalPages - 1}
            className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium ${page >= totalPages - 1 ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-blue-50'}`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-medium">{page * rowsPerPage + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min((page + 1) * rowsPerPage, logs.length)}
              </span>{' '}
              of <span className="font-medium">{logs.length}</span> results
            </p>
          </div>
          <div className="flex items-center">
            <div className="relative mr-6">
              <label
                htmlFor="rowsPerPage"
                className="mr-2 text-sm text-gray-600"
              >
                Rows per page:
              </label>
              <div className="relative inline-block">
                <select
                  id="rowsPerPage"
                  value={rowsPerPage}
                  onChange={onChangeRowsPerPage}
                  className="appearance-none rounded border border-gray-300 bg-white px-3 py-1 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {[10, 25, 50].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <FiChevronDown size={16} />
                </div>
              </div>
            </div>
            <div className="mr-4 flex items-center">
              <label htmlFor="pageInput" className="mr-2 text-sm text-gray-600">
                Go to page:
              </label>
              <input
                id="pageInput"
                type="text"
                value={pageInputValue}
                onChange={onPageInputChange}
                onKeyDown={(event) => onPageInputKeyDown(event, totalPages)}
                className="w-16 rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`1-${totalPages}`}
              />
            </div>
            <nav
              className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={() => onChangePage(page - 1)}
                disabled={page === 0}
                className={`relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium ${page === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-blue-50'}`}
              >
                <span className="sr-only">Previous</span>
                &larr;
              </button>
              {(() => {
                const displayPages = 5;
                let startPage = Math.max(
                  0,
                  Math.min(
                    page - Math.floor(displayPages / 2),
                    totalPages - displayPages,
                  ),
                );

                if (totalPages <= displayPages) {
                  startPage = 0;
                }

                return Array.from(
                  { length: Math.min(displayPages, totalPages) },
                  (_, i) => {
                    const pageNum = startPage + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => onChangePage(pageNum)}
                        className={`flex h-8 w-10 items-center justify-center border ${page === pageNum
                          ? 'z-10 border-blue-500 bg-blue-100 text-blue-600'
                          : 'border-gray-300 bg-white text-gray-500 hover:bg-blue-50'
                          } text-sm font-medium`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  },
                );
              })()}
              <button
                onClick={() => onChangePage(page + 1)}
                disabled={page >= totalPages - 1}
                className={`relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium ${page >= totalPages - 1 ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-blue-50'}`}
              >
                <span className="sr-only">Next</span>
                &rarr;
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuditLog;
